import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';

interface AcceptResult {
  student_first_name: string;
  student_last_name: string;
  inviter_first_name: string;
  needs_onboarding?: boolean;
}

interface FnError {
  context?: { json?: () => Promise<{ error?: string; message?: string }> };
  message?: string;
}

type PageState =
  | { tag: 'loading' }
  | { tag: 'success'; result: AcceptResult }
  | { tag: 'expired' }
  | { tag: 'mismatch' }
  | { tag: 'error'; message: string };

export default function AcceptCoParentInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<PageState>({ tag: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ tag: 'error', message: 'No invite token found in the URL.' });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('accept-co-parent-invite', {
          body: { token },
        });

        if (cancelled) return;

        if (error) {
          const fnErr = error as FnError;
          let code: string | undefined;
          let message = 'Something went wrong. Please try again.';
          if (fnErr?.context?.json) {
            try {
              const body = await fnErr.context.json();
              code = body.error;
              message = body.message ?? message;
            } catch { /* ignore */ }
          } else if (fnErr.message) {
            message = fnErr.message;
          }

          if (code === 'TOKEN_EXPIRED') { setState({ tag: 'expired' }); return; }
          if (code === 'EMAIL_MISMATCH') { setState({ tag: 'mismatch' }); return; }
          setState({ tag: 'error', message });
          return;
        }

        const result = data as AcceptResult;

        // Brand-new co-parent with no profile yet: send them through onboarding to
        // capture a real name + phone. The invite stays pending and the parents
        // AFTER INSERT trigger auto-links it the moment they finish.
        if (result.needs_onboarding) {
          navigate('/onboarding', { replace: true });
          return;
        }

        setState({ tag: 'success', result });
      } catch (err) {
        if (cancelled) return;
        setState({ tag: 'error', message: err instanceof Error ? err.message : 'Something went wrong.' });
      }
    })();

    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
        {/* Loading */}
        {state.tag === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Accepting your invitation…</p>
          </>
        )}

        {/* Success */}
        {state.tag === 'success' && (
          <>
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">You're in!</h1>
            <p className="text-gray-600 mb-1">
              <strong>{state.result.inviter_first_name}</strong> has shared access to{' '}
              <strong>{state.result.student_first_name} {state.result.student_last_name}</strong> with you.
            </p>
            <p className="text-gray-600 mb-6">
              You can now view their enrollments and make payments from your account.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </Link>
          </>
        )}

        {/* Expired */}
        {state.tag === 'expired' && (
          <>
            <Clock className="w-14 h-14 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invite has expired</h1>
            <p className="text-gray-600 mb-6">
              This invitation link has expired (invitations are valid for 90 days).
              Please ask the person who invited you to send a new invite.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Go to Dashboard
            </Link>
          </>
        )}

        {/* Email mismatch */}
        {state.tag === 'mismatch' && (
          <>
            <AlertCircle className="w-14 h-14 text-orange-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Wrong account</h1>
            <p className="text-gray-600 mb-6">
              This invitation was sent to a different email address. Please sign out and sign in with
              the email address that received the invitation.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Go to Dashboard
            </Link>
          </>
        )}

        {/* Generic error */}
        {state.tag === 'error' && (
          <>
            <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">{state.message}</p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Go to Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
